import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  useConfirmPhoneValidationForCustomRegistration,
  useStartPhoneValidationForCustomRegistration,
} from "../../../../hooks/users";
import { refusalMessageKey } from "../../../../utils/refusalMessageKey";
import {
  PHONE_CODE_REFUSALS,
  isCodeSpent,
  refusalValues,
} from "../../../../utils/phoneCodeRefusals";
import { SAVE_REFUSALS } from "../registrationRefusals";

interface PhoneCheckTarget {
  userId: string;
  accessToken: string;
  phoneNumber: string;
}

const CODE_LIFETIME_MS = 5 * 60 * 1000;

const expiryOf = (response: any): number =>
  response?.data?.element?.expiredAt || Date.now() + CODE_LIFETIME_MS;

/**
 * The SMS check of a sign-up: sends the code, shows the code window and, once the number is checked
 * (or cannot be, when the first SMS is refused), goes on with the registration.
 */
export const useRegistrationPhoneCheck = (entityIdentification: string) => {
  const { t } = useTranslation("clientView");
  const [target, setTarget] = useState<PhoneCheckTarget | null>(null);
  const [show, setShow] = useState(false);
  const [expiresAt, setExpiresAt] = useState(0);
  const [codeSpent, setCodeSpent] = useState(false);
  const goOn = useRef<() => Promise<void>>(async () => undefined);

  const { mutate: startValidation, isLoading: isStarting } =
    useStartPhoneValidationForCustomRegistration();
  const { mutate: confirmValidation, isLoading: isConfirming } =
    useConfirmPhoneValidationForCustomRegistration();

  const continueRegistration = useCallback(() => {
    goOn.current().catch((error) => {
      console.error("Registration failed after the phone check:", error);
      toast.error(
        t(
          refusalMessageKey(
            error,
            SAVE_REFUSALS,
            "customRegistration.page.toast.registrationFailed"
          )
        )
      );
    });
  }, [t]);

  const variablesFor = useCallback(
    (who: PhoneCheckTarget) => ({
      entity_identification: entityIdentification,
      user_identification: who.userId,
      token: `Basic ${btoa(`${who.userId}:${who.accessToken}`)}`,
    }),
    [entityIdentification]
  );

  const start = useCallback(
    (who: PhoneCheckTarget, then: () => Promise<void>) => {
      goOn.current = then;
      setTarget(who);
      setCodeSpent(false);
      startValidation(variablesFor(who), {
        onSuccess: (response: any) => {
          setExpiresAt(expiryOf(response));
          setShow(true);
        },
        onError: (error: any) => {
          console.error("Phone validation failed:", error);
          continueRegistration();
        },
      });
    },
    [startValidation, variablesFor, continueRegistration]
  );

  const confirm = useCallback(
    (code: string) => {
      if (!target) return;
      confirmValidation(
        { ...variablesFor(target), code },
        {
          onSuccess: () => {
            setShow(false);
            continueRegistration();
          },
          onError: (error: any) => {
            console.error("Phone validation confirmation failed:", error);
            if (isCodeSpent(error)) setCodeSpent(true);
            toast.error(
              t(
                refusalMessageKey(
                  error,
                  PHONE_CODE_REFUSALS,
                  "customRegistration.page.toast.phoneVerificationFailed"
                ),
                refusalValues(error)
              )
            );
          },
        }
      );
    },
    [target, confirmValidation, variablesFor, continueRegistration, t]
  );

  const resend = useCallback(() => {
    if (!target) return;
    startValidation(variablesFor(target), {
      onSuccess: (response: any) => {
        setExpiresAt(expiryOf(response));
        setCodeSpent(false);
      },
      onError: (error: any) => {
        console.error("Phone validation resend failed:", error);
        toast.error(
          t(
            refusalMessageKey(
              error,
              PHONE_CODE_REFUSALS,
              "customRegistration.page.toast.codeResendFailed"
            ),
            refusalValues(error)
          )
        );
      },
    });
  }, [target, startValidation, variablesFor, t]);

  return {
    start,
    modal: {
      show,
      onClose: () => setShow(false),
      onConfirm: confirm,
      onResend: resend,
      expiresAt,
      codeSpent,
      loading: isStarting || isConfirming,
      phoneNumber: target?.phoneNumber ?? "",
    },
  };
};
